import { bytesToHex } from '@noble/hashes/utils';
import { generateSecretKey, getPublicKey } from 'nostr-tools';
import { nip04, nip19 } from 'nostr-tools';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { z } from 'zod';

import { duellyEventEmitter } from '@/services/eventBus';
import UserService from '@/services/UserService';

import { useAuthStore } from './authStore';
import { useUserProfileStore } from './userProfileStore';
import { useWebSocketStore } from './webSocketStore';

export const MessageContentSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    id: z.string(),
    kind: z.number(),
    created_at: z.number(),
    sender_user_id: z.string(),
    sender_pubkey: z.string(),
    tags: z.array(z.any()),
    content: z.string(),
    seen_by: z.array(z.string()),
});

export type Message = z.infer<typeof MessageContentSchema>;

export const useNostrMessageStore = defineStore(
    'nostrMessageStore',
    () => {
        type ThreadData = {
            messages: Message[];
            seenCount: number;
            lastTimestamp: number;
        };
        const threads = ref<Record<string, ThreadData>>({});
        const lastGlobalMessageTimestamp = ref<number>(0);

        const enrichedProfiles = ref<Record<string, any>>({});
        const nostrKeys = ref<{ privkey: string; pubkey: string; npub: string } | null>(null);

        // === NON-REACTIVE ===
        const seenEventIds = new Set<string>();
        const instanceId = Math.random().toString(36).slice(2); // unique ID each time

        console.log('[nostrMessageStore] CREATED. instanceId =', instanceId);

        // === STORES ===
        const authStore = useAuthStore();
        const user_id = authStore.getUserProfile().id;
        const userProfileStore = useUserProfileStore();
        const wsStore = useWebSocketStore();

        // Load keys from localStorage if they exist
        const storedKeys = localStorage.getItem(`nostr_keys.${user_id}`);
        if (storedKeys) {
            nostrKeys.value = JSON.parse(storedKeys);
        }

        // === COMPUTED ===
        const inboxList = computed(() => {
            const userId = authStore.getUserProfile()?.id;
            if (!userId) return [];

            return Object.entries(threads.value)
                .filter(([threadKey]) => {
                    // only show message for the currently logged in user
                    return threadKey.includes(authStore.getUserProfile().id);
                })
                .map(([threadKey, thread]) => {
                    const last = thread.messages.at(-1);
                    const unreadCount = Math.max(thread.messages.length - thread.seenCount, 0);

                    const ids = threadKey.replace('dm#', '').split('_');
                    const other_user_id = ids.find((id) => id !== user_id) ?? user_id;

                    return {
                        thread_key: threadKey,
                        updated_at: thread.lastTimestamp,
                        last_message: last?.content ?? '',
                        last_message_at: last?.created_at ?? 0,
                        other_user_id,
                        username: enrichedProfiles.value?.[other_user_id]?.username ?? '',
                        profile_photo: enrichedProfiles.value?.[other_user_id]?.profile_photo ?? '',
                        unreadCount,
                    };
                })
                .sort((a, b) => b.updated_at - a.updated_at);
        });

        const totalUnreadMessages = computed(() => {
            const userId = authStore.getUserProfile().id;
            return Object.entries(threads.value)
                .filter(([threadKey]) => threadKey.includes(userId))
                .reduce((total, [, thread]) => {
                    return total + Math.max(thread.messages.length - thread.seenCount, 0);
                }, 0);
        });

        // === ACTIONS ===
        async function generateAndStoreNostrKeys(overide: boolean = false) {
            /*
                First check if the user already has a private key stored in localStorage.
                If so, do not generate a new one.

                If no key pair is found, generate a new one and store it in localStorage.
            */

            // always get the user_id from the authStore to ensure it's up-to-date
            const user_id = authStore.getUserProfile().id;

            if (!overide) {
                console.log('[Nostr] user_id:', user_id);
                const stored = localStorage.getItem(`nostr_keys.${user_id}`);
                console.log('[Nostr] stored:', stored);
                if (stored) {
                    console.log('[Nostr] Local keys found, using them.');
                    nostrKeys.value = JSON.parse(stored);
                    return;
                }

                const remoteProfile = await UserService.profile(user_id, false);
                if (remoteProfile && remoteProfile.npub) {
                    console.log('Remote npub found nostr-import-requierd:', remoteProfile.npub);
                    // emit an event or set a flag: "User has remote npub, needs to import"
                    duellyEventEmitter.emit('nostr-import-required', remoteProfile.npub);
                    return;
                }
            }

            console.log('[Nostr] Generating new keys for user_id:', user_id);
            const rawPrivkey = generateSecretKey();
            const privkey = bytesToHex(rawPrivkey);
            const pubkey = getPublicKey(rawPrivkey);
            const npub = nip19.npubEncode(pubkey);

            const keyObj = { privkey, pubkey, npub };
            localStorage.setItem(`nostr_keys.${user_id}`, JSON.stringify(keyObj));
            nostrKeys.value = keyObj;

            const profile = authStore.getUserProfile();
            profile.npub = npub;
            authStore.setProfile(profile);

            UserService.setProfileDirect(profile.id, profile)
                .then((respo) => {
                    console.log('[Nostr] npub saved to profile:', respo);
                })
                .catch((err) => {
                    console.error('[Nostr] Failed to set npub on profile:', err);
                });
        }

        async function decryptAndStoreMessage(
            event: any,
            source: 'nostr_dm_received' | 'nostr_dm_thread'
        ): Promise<void> {
            console.log('[decryptAndStoreMessage]', event);
            if (
                !event ||
                !event.sender_pubkey ||
                !nostrKeys.value?.privkey ||
                !nostrKeys.value.pubkey
            )
                return;

            try {
                const myPubkey = nostrKeys.value.pubkey;
                let theirPubkey: string | null = null;

                const membersTag = event.tags?.find((tag: string[]) => tag[0] === 'thread_members');
                if (membersTag) {
                    const possibleNpubs = membersTag.slice(1); // skip tag name
                    const otherNpub = possibleNpubs.find((npub: string) => {
                        try {
                            const pub = nip19.decode(npub).data as string;
                            console.log('[decryptAndStoreMessage] pub:', pub);
                            return pub !== myPubkey;
                        } catch {
                            return false;
                        }
                    });

                    if (otherNpub) {
                        theirPubkey = nip19.decode(otherNpub).data as string;
                    }
                }

                // Fallback if not found in thread_members
                if (!theirPubkey && event.sender_pubkey !== myPubkey) {
                    console.log('[decryptAndStoreMessage] Fallback to sender_pubkey');
                    theirPubkey = event.sender_pubkey;
                }

                if (!theirPubkey) {
                    console.warn('[decryptAndStoreMessage] Could not determine other pubkey');
                    event.content = '[Unable to decrypt]';
                } else {
                    event.content = await nip04.decrypt(
                        nostrKeys.value.privkey,
                        theirPubkey,
                        event.content
                    );
                }
            } catch (err) {
                console.error('[decryptAndStoreMessage] Failed to decrypt:', err);
                event.content = '[Unable to decrypt]';
            }

            event._source = source;
            addMessage(['EVENT', 'dm', event]);
        }

        function sync() {
            const userId = authStore.getUserProfile().id;
            const timeSinceLastSync = Date.now() / 1000 - lastGlobalMessageTimestamp.value;
            const buffer = Math.min(Math.max(timeSinceLastSync, 60), 250000); // 1 minute to 250k seconds

            const since = lastGlobalMessageTimestamp.value - buffer;
            console.log('[Nostr] Syncing messages since:', since);

            wsStore.wsService?.reqNostrEvents('inbox-preview', {
                kinds: [4],
                since: 0,
                // since: since,
                '#dm_inbox_user': userId,
                limit: 100,
            });
        }

        function ensureProfileLoaded(userId: string) {
            if (!enrichedProfiles.value[userId]) {
                userProfileStore
                    .getPublicProfile(userId)
                    .then((profile) => {
                        if (profile) enrichedProfiles.value[userId] = profile;
                    })
                    .catch((err) => {
                        console.warn(`[Nostr] Failed to fetch profile for ${userId}:`, err);
                    });
            }
        }

        function addInboxPreview(event: any) {
            console.log('[addInboxPreview]', event);
            if (!event?.thread_key || !event?.sender_user_id || !event?.created_at) {
                console.warn('[addInboxPreview] [Nostr] Malformed event:', event);
                return;
            }
            if (threads.value[event.thread_key]) {
                console.log(
                    `[InboxPreview] Thread ${event.thread_key} already loaded. Skipping fetch.`
                );
                return;
            }

            const existingThread = threads.value[event.thread_key] || {
                messages: [],
                seenCount: 0,
                lastTimestamp: 0,
            };
            threads.value[event.thread_key] = {
                ...existingThread,
                lastTimestamp: Math.max(existingThread.lastTimestamp, event.created_at),
            };

            // TODO THIS NEEDS CLEAN UP
            // get sender_user_id from event.tags array
            const senderUserId = event.sender_user_id;
            if (!senderUserId) {
                console.warn('[addMessage] [Nostr] No sender_user_id found in event:', event);
                return;
            }
            ensureProfileLoaded(senderUserId);
        }

        function addMessage(data: any) {
            console.log('[addMessage]', data);
            if (!Array.isArray(data) || data[0] !== 'EVENT') {
                return;
            }

            const parseResult = MessageContentSchema.safeParse(data[2]);
            if (!parseResult.success) {
                console.warn('[addMessage] Failed to validate MessageContent:', parseResult.error);
                return;
            }
            const parsedMessage = parseResult.data;

            const PK = parsedMessage.PK;
            const SK = parsedMessage.SK;

            const threadMessages = threads.value[parsedMessage.PK]?.messages || [];
            const exists = threadMessages.some((item) => item.PK === PK && item.SK === SK);
            if (exists) {
                console.log(
                    '[addMessage] [Nostr] Message already exists in thread:',
                    parsedMessage
                );
                return;
            }
            console.log(
                '[addMessage] [Nostr] threadMessages.length:',
                threadMessages.length,
                threadMessages
            );

            // Skip if we've seen this event
            if (seenEventIds.has(parsedMessage.id)) {
                console.log('[addMessage] [Nostr] Event already seen, skipping:', parsedMessage.id);
                return;
            }
            seenEventIds.add(parsedMessage.id);

            const senderUserId = parsedMessage.sender_user_id;
            if (!senderUserId) {
                console.warn(
                    '[addMessage] [Nostr] No sender_user_id found in event:',
                    parsedMessage
                );
                return;
            }
            ensureProfileLoaded(senderUserId);

            // Store the parsedMessage
            const threadKey = parsedMessage.PK;
            if (!threads.value[threadKey]) {
                addInboxPreview(parsedMessage);
            }

            threads.value[threadKey].messages.push(parsedMessage);

            // Optionally update lastTimestamp too
            threads.value[threadKey].lastTimestamp = Math.max(
                threads.value[threadKey].lastTimestamp,
                parsedMessage.created_at
            );

            lastGlobalMessageTimestamp.value = Math.max(
                lastGlobalMessageTimestamp.value,
                parsedMessage.created_at
            );
        }

        // === ACTIONS ===
        function setNostrKeys(keys: { privkey: string; pubkey: string; npub: string }) {
            nostrKeys.value = keys;
        }

        function markThreadAsRead(threadKey: string) {
            const thread = threads.value[threadKey];
            if (thread) {
                thread.seenCount = thread.messages.length;
            }
        }

        return {
            // State
            nostrKeys,
            enrichedProfiles,
            threads,
            totalUnreadMessages,

            // Computed
            inboxList,

            // Actions
            addMessage,
            addInboxPreview,
            decryptAndStoreMessage,
            generateAndStoreNostrKeys,
            sync,
            setNostrKeys,
            markThreadAsRead,
        };
    },
    {
        persist: true,
    }
);
